import React from 'react';
import { ChefHat, Clock, CheckCircle2, TrendingUp, ArrowRight, Package, RefreshCw, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOrders } from '../contexts/OrderContext';
import { displayProductName } from '../utils/formatters';
import { filterTodayOrders } from '../utils/dateFilters';
import { useDocumentTitle } from '../utils/useDocumentTitle';
const KANBAN_COLUMNS = [{
  id: 'Pendiente',
  label: 'Pendiente',
  color: 'yellow',
  borderColor: 'border-l-yellow-400 dark:border-l-yellow-500',
  bgColor: 'bg-yellow-50 dark:bg-yellow-900/10',
  textColor: 'text-yellow-700 dark:text-yellow-400',
  dotColor: 'bg-yellow-500',
  nextStatus: 'En Preparación'
}, {
  id: 'En Preparación',
  label: 'En Preparación',
  color: 'blue',
  borderColor: 'border-l-blue-400 dark:border-l-blue-500',
  bgColor: 'bg-blue-50 dark:bg-blue-900/10',
  textColor: 'text-blue-700 dark:text-blue-400',
  dotColor: 'bg-blue-500',
  nextStatus: 'Listo'
}, {
  id: 'Listo',
  label: 'Listo',
  color: 'green',
  borderColor: 'border-l-green-400 dark:border-l-green-500',
  bgColor: 'bg-green-50 dark:bg-green-900/10',
  textColor: 'text-green-700 dark:text-green-400',
  dotColor: 'bg-green-500',
  nextStatus: 'Entregado'
}, {
  id: 'Entregado',
  label: 'Entregado',
  color: 'gray',
  borderColor: 'border-l-gray-300 dark:border-l-gray-600',
  bgColor: 'bg-gray-50 dark:bg-gray-800/50',
  textColor: 'text-gray-600 dark:text-gray-400',
  dotColor: 'bg-gray-400',
  nextStatus: null
}];
export function ProductionView() {
  useDocumentTitle('Producción');
  const {
    orders,
    updateOrderStatus
  } = useOrders();
  // Filter orders: only today's orders, exclude cancelled
  const todayOrders = filterTodayOrders(orders).filter(o => o.status !== 'Cancelado');
  // Calculate production stats
  const pendingOrders = todayOrders.filter(o => o.status === 'Pendiente' || o.status === 'En Preparación');
  const completedOrders = todayOrders.filter(o => o.status === 'Listo' || o.status === 'Entregado');
  const totalPending = pendingOrders.length;
  const totalCompleted = completedOrders.length;
  const totalOrders = todayOrders.length;
  const progressPercent = totalOrders > 0 ? Math.round(totalCompleted / totalOrders * 100) : 0;
  // Group orders by status
  const ordersByStatus = KANBAN_COLUMNS.reduce((acc, col) => {
    acc[col.id] = todayOrders.filter(o => o.status === col.id);
    return acc;
  }, {} as Record<string, any[]>);
  const handleAdvanceStatus = (orderId: string, nextStatus: string | null) => {
    if (nextStatus) {
      updateOrderStatus(orderId, nextStatus as any);
    }
  };
  return <div className="p-4 lg:p-8 max-w-[1800px] mx-auto min-h-[calc(100vh-4rem)] flex flex-col" data-id="element-5281">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-5282">
        <div data-id="element-5283">
          <div className="flex items-center gap-2" data-id="element-5284">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5285">
              Producción - Hoy
            </h1>
            <RefreshCw className="w-4 h-4 text-gray-400" data-id="element-5286" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5287">
            Gestiona el flujo de preparación y entrega de pedidos
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-5288">
        <div className="bg-white dark:bg-gray-900 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800" data-id="element-5289">
          <div className="flex items-center justify-between mb-3" data-id="element-5290">
            <div className="p-2.5 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-5291">
              <Package className="w-5 h-5 text-gray-700 dark:text-gray-300" data-id="element-5292" />
            </div>
            <TrendingUp className="w-4 h-4 text-gray-400" data-id="element-5293" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1" data-id="element-5294">
            Total Pedidos Hoy
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5295">
            {totalOrders}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-900 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800" data-id="element-5296">
          <div className="flex items-center justify-between mb-3" data-id="element-5297">
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-5298">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-5299" />
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1" data-id="element-5300">
            En Proceso
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5301">
            {totalPending}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-900 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800" data-id="element-5302">
          <div className="flex items-center justify-between mb-3" data-id="element-5303">
            <div className="p-2.5 bg-green-50 dark:bg-green-900/20 rounded-lg" data-id="element-5304">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-5305" />
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1" data-id="element-5306">
            Completados
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5307">
            {totalCompleted}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-900 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 flex flex-col justify-center" data-id="element-5308">
          <div className="flex justify-between items-end mb-2" data-id="element-5309">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5310">
              Progreso del día
            </p>
            <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5311">
              {progressPercent}%
            </span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 mb-2" data-id="element-5312">
            <div className="bg-gray-900 dark:bg-white h-2.5 rounded-full transition-all duration-500" style={{
            width: `${progressPercent}%`
          }} data-id="element-5313"></div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-6 items-start pb-8" data-id="element-5314">
        {KANBAN_COLUMNS.map(column => {
        const columnOrders = ordersByStatus[column.id] || [];
        return <div key={column.id} className="flex flex-col bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-gray-200/60 dark:border-gray-800/60 h-[600px] lg:h-[calc(100vh-320px)] overflow-hidden" data-id="element-5315">
              {/* Column Header */}
              <div className="p-4 border-b border-gray-200/60 dark:border-gray-800/60 bg-gray-50/80 dark:bg-gray-900/50 backdrop-blur-sm sticky top-0 z-10" data-id="element-5316">
                <div className="flex items-center justify-between" data-id="element-5317">
                  <div className="flex items-center gap-2" data-id="element-5318">
                    <span className={`w-2 h-2 rounded-full ${column.dotColor}`} data-id="element-5319" />
                    <h2 className="font-bold text-gray-900 dark:text-white" data-id="element-5320">
                      {column.label}
                    </h2>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-white dark:bg-gray-800 text-xs font-bold text-gray-600 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-gray-700" data-id="element-5321">
                    {columnOrders.length}
                  </span>
                </div>
              </div>

              {/* Column Content (Scrollable) */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700" data-id="element-5322">
                <AnimatePresence mode="popLayout" data-id="element-5323">
                  {columnOrders.length === 0 ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="h-32 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-xl m-1" data-id="element-5324">
                      <AlertCircle className="w-6 h-6 mb-2 opacity-50" data-id="element-5325" />
                      <p className="text-sm font-medium" data-id="element-5326">Sin pedidos</p>
                    </motion.div> : columnOrders.map(order => {
                const productParts = order.product.split(' | ').map((p: string) => p.trim()).filter(Boolean);
                const mainProduct = displayProductName(productParts[0] || order.product);
                const selections = productParts.slice(1);
                return <motion.div layout initial={{
                  opacity: 0,
                  y: 20,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.2
                }} key={order.id} className={`bg-white dark:bg-gray-900 p-4 rounded-xl shadow-sm border border-l-4 ${column.borderColor} border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow group`} data-id="element-5327">
                          <div className="flex justify-between items-start mb-3" data-id="element-5328">
                            <div className="flex items-center gap-2" data-id="element-5329">
                              <span className="text-xs font-mono font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded" data-id="element-5330">
                                #{order.orderNo || order.order_no}
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500" data-id="element-5331">
                                {order.orderDate?.split(',')[1] || order.orderDate}
                              </span>
                            </div>

                            {column.nextStatus && <button onClick={() => handleAdvanceStatus(order.id, column.nextStatus)} className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-md transition-colors ${column.bgColor} ${column.textColor} hover:brightness-95 dark:hover:brightness-110 cursor-pointer`} title={`Avanzar a ${column.nextStatus}`} data-id="element-5332">
                                Avanzar
                                <ArrowRight className="w-3 h-3" data-id="element-5333" />
                              </button>}
                          </div>

                          <div className="mb-3" data-id="element-5334">
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight mb-1" data-id="element-5335">
                              {mainProduct}
                            </h3>
                            {selections.length > 0 && <div className="space-y-0.5 mb-2" data-id="element-5336">
                                {selections.map((sel: string, i: number) => <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400" data-id="element-5337">
                                    <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600 shrink-0" data-id="element-5338" />
                                    <span className="truncate" data-id="element-5339">{sel}</span>
                                  </div>)}
                              </div>}
                          </div>

                          <div className="flex items-end justify-between pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-5340">
                            <div className="min-w-0 flex-1 pr-3" data-id="element-5341">
                              <p className="text-xs font-medium text-gray-900 dark:text-white truncate" data-id="element-5342">
                                {order.name || order.employee_name}
                              </p>
                              <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate" data-id="element-5343">
                                {order.company}
                                {order.branch ? ` • ${order.branch}` : ''}
                              </p>
                            </div>
                            <div className="text-right shrink-0" data-id="element-5344">
                              <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5" data-id="element-5345">
                                Cant.
                              </p>
                              <p className="text-lg font-bold text-gray-900 dark:text-white leading-none" data-id="element-5346">
                                {order.quantity}
                              </p>
                            </div>
                          </div>
                        </motion.div>;
              })}
                </AnimatePresence>
              </div>
            </div>;
      })}
      </div>
    </div>;
}